import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { currency } from "@/lib/constants";
import Sparkline from "@/components/Sparkline";
import { BarChart3, DollarSign, FileText, Zap, RefreshCw, Send, Trash2, Plus, X, Play, Clock, Share2, Link as LinkIcon } from "lucide-react";

const GROUPS = [
  ["department", "Department"], ["contract_type", "Contract Type"],
  ["assigned_attorney_name", "Attorney"], ["stage", "Stage"], ["vendor_legal_name", "Vendor"],
];
const METRICS = [["count", "Count"], ["sum_amount", "Total $ value"], ["avg_cycle", "Avg cycle days"]];

export default function LeadershipDashboard() {
  const nav = useNavigate();
  const [scorecard, setScorecard] = useState(null);
  const [days, setDays] = useState(90);

  // Builder state
  const [groupBy, setGroupBy] = useState("department");
  const [metric, setMetric] = useState("count");
  const [dept, setDept] = useState("");
  const [stage, setStage] = useState("");
  const [built, setBuilt] = useState(null);

  // Scheduled state
  const [scheds, setScheds] = useState([]);
  const [showSched, setShowSched] = useState(false);
  const [schedForm, setSchedForm] = useState({ name: "", cadence: "weekly", recipients: "", filters: {}, group_by: "department", metric: "count", active: true });

  const loadAll = () => {
    api.get(`/reports/leadership?days=${days}`).then(r => setScorecard(r.data)).catch(() => toast.error("Scorecard failed"));
    api.get("/scheduled-reports").then(r => setScheds(r.data || [])).catch(() => {});
  };
  useEffect(() => { loadAll(); }, [days]);

  const runBuild = async () => {
    try {
      const { data } = await api.post("/reports/leadership/build", { group_by: groupBy, metric, filters: { department: dept || undefined, stage: stage || undefined } });
      setBuilt(data);
    } catch { toast.error("Failed to build"); }
  };

  const saveSched = async () => {
    if (!schedForm.name.trim() || !schedForm.recipients.trim()) return toast.error("Name + recipients required");
    const body = {
      ...schedForm,
      recipients: schedForm.recipients.split(",").map(s => s.trim()).filter(Boolean),
      filters: { department: dept || undefined, stage: stage || undefined },
      group_by: groupBy, metric,
    };
    try { await api.post("/scheduled-reports", body); toast.success("Scheduled"); setShowSched(false); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const sendNow = async (s) => {
    try { const { data } = await api.post(`/scheduled-reports/${s.id}/send-now`); toast.success(`Sent ${data.sent} email(s)`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  const shareLink = async (s) => {
    try {
      const { data } = await api.post(`/scheduled-reports/${s.id}/share`);
      const url = `${window.location.origin}${data.share_url}`;
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch { toast.info(url); }
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revokeShare = async (s) => {
    if (!window.confirm(`Revoke the public share link for "${s.name}"?`)) return;
    try { await api.delete(`/scheduled-reports/${s.id}/share`); loadAll(); }
    catch { toast.error("Failed"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await api.delete(`/scheduled-reports/${s.id}`); loadAll(); }
    catch { toast.error("Failed"); }
  };

  if (!scorecard) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;
  const k = scorecard.kpis;

  return (
    <div className="p-6 space-y-6" data-testid="leadership-page">
      <div className="flex items-center gap-3">
        <BarChart3 className="w-5 h-5 text-[#005289]"/>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Phase 11 · Leadership</div>
          <h1 className="text-2xl font-bold text-slate-900">Executive Scorecard</h1>
        </div>
        <select value={days} onChange={e => setDays(+e.target.value)} data-testid="ld-period"
                className="ml-auto h-9 px-2 border border-slate-200 rounded text-xs">
          {[30, 60, 90, 180, 365].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
        <button onClick={loadAll} className="h-9 px-3 text-xs border border-slate-200 rounded"><RefreshCw className="w-3 h-3 inline"/></button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total Contracts" value={k.total_contracts} icon={FileText} tone="blue" trend={scorecard.trends?.new} onClick={() => nav("/central")} />
        <KPI label="Executed" value={k.executed} icon={FileText} tone="green" trend={scorecard.trends?.executed} onClick={() => nav("/pipeline?stage=executed")} />
        <KPI label="In Progress" value={k.in_progress} icon={Clock} tone="amber" trend={scorecard.trends?.new} onClick={() => nav("/pipeline")} />
        <KPI label="SLA Breach" value={k.sla_breach} icon={Zap} tone="red" onClick={() => nav("/reports")} />
        <KPI label={`New in ${days}d`} value={k.new_period} icon={Plus} tone="blue" trend={scorecard.trends?.new} onClick={() => nav("/central")} />
        <KPI label={`Executed in ${days}d`} value={k.executed_period} icon={FileText} tone="green" trend={scorecard.trends?.executed} onClick={() => nav("/pipeline?stage=executed")} />
        <KPI label="Avg Cycle Days" value={k.avg_cycle_days != null ? `${k.avg_cycle_days}d` : "—"} icon={Clock} tone="slate" onClick={() => nav("/reports")} />
        <KPI label="Renewals ≤ 90d" value={k.renewals_next_90d} icon={RefreshCw} tone="amber" onClick={() => nav("/renewals")} />
        <KPI label="YTD Pipeline $" value={currency(k.ytd_pipeline_value || 0)} icon={DollarSign} tone="blue" wide onClick={() => nav("/central")} />
        <KPI label="YTD Executed $" value={currency(k.ytd_executed_value || 0)} icon={DollarSign} tone="green" wide onClick={() => nav("/pipeline?stage=executed")} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MiniTable title="By Department" rows={scorecard.by_department} />
        <MiniTable title="By Contract Type" rows={scorecard.by_contract_type} />
        <MiniTable title="By Attorney" rows={scorecard.by_attorney} />
      </div>

      {/* Custom Report Builder */}
      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="report-builder">
        <div className="text-xs font-bold uppercase tracking-wider text-[#005289] mb-3">Custom Report Builder</div>
        <div className="grid grid-cols-5 gap-2 text-xs">
          <label className="block"><span className="text-slate-500 text-[10px]">Group by</span>
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)} data-testid="rb-group"
                    className="w-full h-9 px-2 border border-slate-200 rounded">
              {GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-slate-500 text-[10px]">Metric</span>
            <select value={metric} onChange={e => setMetric(e.target.value)} data-testid="rb-metric"
                    className="w-full h-9 px-2 border border-slate-200 rounded">
              {METRICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-slate-500 text-[10px]">Department (filter)</span>
            <input value={dept} onChange={e => setDept(e.target.value)} placeholder="Any" data-testid="rb-dept"
                   className="w-full h-9 px-2 border border-slate-200 rounded"/>
          </label>
          <label className="block"><span className="text-slate-500 text-[10px]">Stage (filter)</span>
            <input value={stage} onChange={e => setStage(e.target.value)} placeholder="Any" data-testid="rb-stage"
                   className="w-full h-9 px-2 border border-slate-200 rounded"/>
          </label>
          <div className="flex items-end gap-2">
            <button onClick={runBuild} data-testid="rb-run" className="h-9 px-3 rounded bg-[#005289] text-white text-xs font-semibold flex items-center gap-1"><Play className="w-3 h-3"/>Run</button>
            <button onClick={() => setShowSched(true)} data-testid="rb-schedule" className="h-9 px-3 rounded border border-[#005289] text-[#005289] text-xs font-semibold flex items-center gap-1"><Send className="w-3 h-3"/>Schedule</button>
          </div>
        </div>
        {built ? (
          <div className="mt-3 border border-slate-200 rounded overflow-x-auto" data-testid="rb-result">
            <table className="hcd-table text-xs w-full">
              <thead><tr><th>{built.group_by}</th><th className="text-right">Count</th>{built.metric !== "count" ? <th className="text-right">{built.metric === "sum_amount" ? "Value" : "Avg days"}</th> : null}</tr></thead>
              <tbody>
                {built.rows.length === 0 ? <tr><td colSpan={3} className="text-center text-slate-400 py-4">No data</td></tr> :
                  built.rows.map((r, i) => (
                    <tr key={i}><td>{r.key}</td><td className="text-right font-mono">{r.count}</td>{built.metric !== "count" ? <td className="text-right font-mono">{built.metric === "sum_amount" ? currency(r.value || 0) : (r.value || 0).toFixed(1) + "d"}</td> : null}</tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {/* Scheduled Reports */}
      <div className="bg-white border border-slate-200 rounded-md" data-testid="scheduled-list">
        <div className="px-4 py-2 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
          Scheduled Reports ({scheds.length})
          <span className="ml-auto text-[10px] font-normal text-slate-400 normal-case">Delivered via Resend</span>
        </div>
        <div className="divide-y divide-slate-100">
          {scheds.length === 0 ? <div className="p-6 text-center text-xs text-slate-400">No scheduled reports yet.</div> : null}
          {scheds.map(s => (
            <div key={s.id} className="px-4 py-3 flex items-center gap-3 text-xs" data-testid={`sched-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{s.name}</div>
                <div className="text-[11px] text-slate-500">{s.cadence} · group by {s.group_by} · {s.metric} · {s.recipients?.length || 0} recipient(s)</div>
                <div className="text-[10px] text-slate-400">Last sent: {s.last_sent_at ? new Date(s.last_sent_at).toLocaleString() : "never"}</div>
              </div>
              <button onClick={() => sendNow(s)} data-testid={`sched-send-${s.id}`} className="h-7 px-2 text-[10px] rounded bg-[#005289] text-white flex items-center gap-1"><Send className="w-3 h-3"/>Send now</button>
              {s.share_token ? (
                <button onClick={() => revokeShare(s)} data-testid={`sched-revoke-${s.id}`} className="h-7 px-2 text-[10px] rounded bg-amber-500 text-white flex items-center gap-1" title={`${window.location.origin}/share/report/${s.share_token}`}><LinkIcon className="w-3 h-3"/>Revoke link</button>
              ) : (
                <button onClick={() => shareLink(s)} data-testid={`sched-share-${s.id}`} className="h-7 px-2 text-[10px] rounded border border-slate-300 flex items-center gap-1"><Share2 className="w-3 h-3"/>Share link</button>
              )}
              <button onClick={() => del(s)} data-testid={`sched-del-${s.id}`} className="h-7 px-2 text-[10px] border border-slate-200 rounded text-red-600 flex items-center gap-1"><Trash2 className="w-3 h-3"/></button>
            </div>
          ))}
        </div>
      </div>

      {showSched ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowSched(false)} data-testid="sched-modal">
          <div className="bg-white rounded-md w-[520px]" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center gap-2"><div className="font-bold text-sm">Schedule this report</div><button onClick={() => setShowSched(false)} className="ml-auto"><X className="w-4 h-4"/></button></div>
            <div className="p-5 space-y-3 text-xs">
              <label className="block"><span className="font-bold block mb-1">Name</span>
                <input value={schedForm.name} onChange={e => setSchedForm({...schedForm, name: e.target.value})} data-testid="sched-name" className="w-full h-9 px-3 border border-slate-200 rounded"/>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block"><span className="font-bold block mb-1">Cadence</span>
                  <select value={schedForm.cadence} onChange={e => setSchedForm({...schedForm, cadence: e.target.value})} data-testid="sched-cadence" className="w-full h-9 px-2 border border-slate-200 rounded">
                    <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                  </select>
                </label>
                <label className="flex items-end pb-1 gap-2"><input type="checkbox" checked={schedForm.active} onChange={e => setSchedForm({...schedForm, active: e.target.checked})}/><span>Active</span></label>
              </div>
              <label className="block"><span className="font-bold block mb-1">Recipients (comma-separated emails)</span>
                <input value={schedForm.recipients} onChange={e => setSchedForm({...schedForm, recipients: e.target.value})} placeholder="ceo@hcdpbc.org, gc@hcdpbc.org" data-testid="sched-recipients"
                       className="w-full h-9 px-3 border border-slate-200 rounded font-mono"/>
              </label>
              <div className="text-[11px] text-slate-500">Report snapshot: group by <b>{groupBy}</b>, metric <b>{metric}</b>{dept ? `, department "${dept}"` : ""}{stage ? `, stage "${stage}"` : ""}.</div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setShowSched(false)} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
              <button onClick={saveSched} data-testid="sched-save" className="h-9 px-4 rounded bg-[#005289] text-white text-xs font-semibold">Save</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const TONES = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
  slate: "bg-slate-50 text-slate-700 border-slate-200",
};
function KPI({ label, value, icon: Icon, tone = "slate", wide, trend, onClick }) {
  return (
    <div onClick={onClick} data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
         className={`border rounded-md p-3 ${TONES[tone]} ${wide ? "md:col-span-2" : ""} ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider opacity-75"><Icon className="w-3 h-3"/>{label}</div>
      <div className="text-2xl font-bold mt-1">{value ?? "—"}</div>
      {trend && trend.length ? <div className="mt-1"><Sparkline values={trend} width={wide ? 200 : 100} height={22} color="currentColor"/></div> : null}
    </div>
  );
}
function MiniTable({ title, rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-4 py-2 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
        {(!rows || rows.length === 0) ? <div className="p-4 text-center text-xs text-slate-400">No data</div> : null}
        {(rows || []).map((r, i) => (
          <div key={i} className="px-4 py-2 flex items-center text-xs">
            <div className="flex-1 truncate">{r.key}</div>
            <div className="font-mono text-slate-600">{r.count}</div>
            {r.value != null ? <div className="font-mono text-slate-500 ml-3">{currency(r.value)}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}
